import React from "react";
import { Box, Icon, Text } from "@salestrak/ui";
import type { IconName } from "@salestrak/ui";

/**
 * The panel's callout band — a muted plate with a tinted glyph tile, a title,
 * one line of detail and trailing actions. Shared by the Org Chart tab's
 * Explore band and the Overview's pending-invitation notice.
 */
export function PanelBanner({
  icon,
  tone = "brand",
  layout = "row",
  title,
  description,
  actions,
}: {
  icon: IconName;
  /** Tile tint: `brand` for a way forward, `warning` for a state that wants
   *  attention. Neither is a Color union member, so both travel as utilities. */
  tone?: "brand" | "warning";
  /** `row` seats one action beside the copy; `stack` drops a wider action
   *  cluster onto its own line under the copy, so a 370px island never
   *  wraps the title against two buttons. */
  layout?: "row" | "stack";
  title: string;
  description: React.ReactNode;
  actions?: React.ReactNode;
}) {
  const stacked = layout === "stack";
  return (
    <Box
      align={stacked ? undefined : "center"}
      background="muted"
      direction={stacked ? "column" : "row"}
      gap={stacked ? "sm" : "md"}
      justify={stacked ? undefined : "between"}
      padding="base"
      rounded="lg"
      shrink={false}
    >
      <Box align="center" direction="row" gap="base" minWidth={0} shrink>
        {/* Explicit tile so the glyph gets real breathing room. */}
        <Box
          align="center"
          className={tone === "warning" ? "bg-warning/10" : "bg-brand/10"}
          direction="row"
          height={40}
          justify="center"
          rounded="lg"
          shrink={false}
          width={40}
        >
          <Icon
            className={tone === "warning" ? "text-warning" : "text-brand"}
            name={icon}
          />
        </Box>
        <Box direction="column" gap="2xs" minWidth={0} shrink>
          <Text size="sm" weight="semibold">
            {title}
          </Text>
          {/* A node (a loading bar while the detail is in flight) stands in
              the line on its own; copy gets the muted xs rung. */}
          {typeof description === "string" ? (
            <Text size="xs" variant="muted">
              {description}
            </Text>
          ) : (
            description
          )}
        </Box>
      </Box>
      {actions ? (
        <Box
          align="center"
          direction="row"
          gap="xs"
          justify={stacked ? "end" : undefined}
          shrink={false}
        >
          {actions}
        </Box>
      ) : null}
    </Box>
  );
}
